// Datos de ejemplo de la guía a fondo del vendedor (D-141): una vendedora con experiencia.
// Solo se usan en las rutas /vendedor/tutorial/... (ver demo.interceptor.ts). Tipados con los
// modelos reales: si un modelo cambia, el compilador avisa aquí.
import type { ImagenPublica } from '../../core/models/catalogo.model';
import type { EstadoCuenta } from '../vendedor/cobro/estado-cuenta.model';
import type { OrdenPago, PanelCiclo, PedidoCiclo, PedidoCicloResumen } from '../vendedor/cobro/cobro.model';
import type { Canje, IdeaPremio, MisPuntos, Premio } from '../vendedor/premios/premios.model';
import type { MiRed } from '../vendedor/red/red.model';
import type { Cliente, PedidoResumen, ResumenVendedor } from '../vendedor/vendedor.model';

const DIA = 86_400_000;
const hace = (dias: number, horas = 0) => new Date(Date.now() - dias * DIA - horas * 3_600_000).toISOString();
const en = (dias: number, horas = 0) => new Date(Date.now() + dias * DIA + horas * 3_600_000).toISOString();
const img = (n: number): ImagenPublica => ({ id: `demo-img-${n}`, url: `tutorial/prenda-${n}.svg` });

// ── Clientes ─────────────────────────────────────────────────────────────────
const cliente = (id: string, nombre: string, telefono: string | null, zona: string, tallas: Cliente['tallas_habituales'], pedidos: number, dias: number): Cliente => ({
  id,
  nombre,
  telefono,
  direccion: null,
  zona,
  tallas_habituales: tallas,
  medidas: null,
  notas: null,
  anonimizado_en: null,
  version: 1,
  created_at: hace(dias),
  updated_at: hace(2),
  pedidos,
});
export const CLIENTES: Cliente[] = [
  cliente('demo-c1', 'María José Rangel', '+584141234567', 'Chacao', { superior: 'M', inferior: '30', calzado: '38' }, 9, 160),
  cliente('demo-c2', 'Andreína Pérez', '+584242345678', 'El Cafetal', { superior: 'S', inferior: '28' }, 6, 120),
  cliente('demo-c3', 'Gabriela Torres', '+584123456789', 'Los Palos Grandes', { superior: 'L', calzado: '39' }, 4, 90),
  cliente('demo-c4', 'Carlos Mendoza', '+584165551234', 'La Candelaria', { superior: 'L', inferior: '34' }, 3, 75),
  cliente('demo-c5', 'Valentina Ruiz', null, 'Altamira', { superior: 'XS' }, 2, 40),
  cliente('demo-c6', 'Patricia Gómez', '+584147778899', 'Baruta', { superior: 'M', calzado: '37' }, 1, 12),
];

// ── Pedidos de clientes ──────────────────────────────────────────────────────
const pedido = (id: string, c: Cliente, estado: PedidoResumen['estado'], origen: PedidoResumen['origen'], prendas: number, total: string, dias: number): PedidoResumen => ({
  id,
  estado,
  origen,
  cliente: { id: c.id, nombre: c.nombre, telefono: c.telefono },
  prendas,
  total_usd: total,
  created_at: hace(dias),
  updated_at: hace(Math.max(0, dias - 1)),
});
export const PEDIDOS: PedidoResumen[] = [
  pedido('demo-p1', CLIENTES[5], 'ABIERTO', 'CARRITO', 3, '41.50', 0),
  pedido('demo-p2', CLIENTES[2], 'ABIERTO', 'MANUAL', 2, '27.00', 1),
  pedido('demo-p3', CLIENTES[0], 'EN_CICLO', 'CARRITO', 4, '58.00', 4),
  pedido('demo-p4', CLIENTES[1], 'EN_CICLO', 'MANUAL', 3, '44.50', 5),
  pedido('demo-p5', CLIENTES[3], 'EN_CICLO', 'CARRITO', 2, '31.00', 6),
  pedido('demo-p6', CLIENTES[0], 'CERRADO', 'CARRITO', 5, '72.50', 21),
  pedido('demo-p7', CLIENTES[4], 'CERRADO', 'MANUAL', 2, '23.00', 22),
  pedido('demo-p8', CLIENTES[1], 'CANCELADO', 'MANUAL', 1, '15.50', 25),
];
export const RESUMEN: ResumenVendedor = { pedidos_abiertos: 2, prendas_abiertas: 5, total_abierto_usd: '68.50', clientes: CLIENTES.length, ultimos_abiertos: PEDIDOS.slice(0, 2) };

// ── Ciclo actual: un pedido pagado y dos clientes nuevos para un pedido adicional ──
const ciclo = { id: 'demo-ciclo', nombre: 'Ciclo 15 de octubre', estado: 'ABIERTO' as const, apertura_en: hace(9), cierre_en: en(3, 5), gracia_horas: 24 };

const orden = (id: string, estado: OrdenPago['estado'], usd: string, ves: string, dias: number, extra: Partial<OrdenPago> = {}): OrdenPago => ({
  id,
  pedido_ciclo_id: 'demo-pc1',
  metodo: 'PAGO_MOVIL',
  monto_usd: usd,
  tasa_valor: '191.23000000',
  monto_ves: ves,
  factor_divisas: null,
  monto_usd_fisico: null,
  vence_en: hace(dias - 1),
  estado,
  comprobante_tipo: estado === 'ABIERTA' ? null : 'image/webp',
  comprobante_cargado_en: estado === 'ABIERTA' ? null : hace(dias, -2),
  referencia: estado === 'ABIERTA' ? null : '004512',
  banco_origen: estado === 'ABIERTA' ? null : '0134',
  fecha_pago: estado === 'ABIERTA' ? null : hace(dias, -1),
  monto_ves_declarado: estado === 'ABIERTA' ? null : ves,
  monto_usd_fisico_declarado: null,
  posible_duplicado: false,
  credito_usd: estado === 'APROBADA' ? usd : null,
  validada_en: estado === 'APROBADA' ? hace(dias, -5) : null,
  motivo_rechazo: null,
  detalle_rechazo: null,
  created_at: hace(dias),
  ...extra,
});

export const PEDIDO_CICLO: PedidoCiclo = {
  id: 'demo-pc1',
  estado: 'CONFIRMADO',
  estado_pago: 'ABONO_MINIMO',
  ciclo,
  totales: { total_publico_usd: '133.50', comision_vendedor_usd: '20.00', total_a_pagar_usd: '113.50', comision_referido_usd: '3.00', puntos_cop: '11250', unidades: 9 },
  pct_abono_minimo: '0.250000',
  abono_minimo_usd: '28.3750',
  saldo: { cargo_neto_usd: '113.5000', abonado_usd: '45.0000', saldo_usd: '68.5000' },
  pendiente: { saldo_usd: '68.5000', abonado_usd: '45.0000', falta_minimo_usd: '0.0000' },
  pedidos_cliente: [
    { id: 'demo-p3', estado: 'EN_CICLO', cliente_nombre: CLIENTES[0].nombre },
    { id: 'demo-p4', estado: 'EN_CICLO', cliente_nombre: CLIENTES[1].nombre },
    { id: 'demo-p5', estado: 'EN_CICLO', cliente_nombre: CLIENTES[3].nombre },
  ],
  items: [
    { id: 'demo-i1', pedido_cliente_id: 'demo-p3', cantidad: 2, estado: 'PENDIENTE', motivo_quitado: null, precio_usd: '14.50', precio_nuevo_usd: null, etiqueta: 'Negro, M', producto: { id: 'demo-pr1', titulo: 'Vestido midi de tirantes con abertura lateral', color_nombre: 'Negro', imagen: img(1) } },
    { id: 'demo-i2', pedido_cliente_id: 'demo-p3', cantidad: 2, estado: 'PENDIENTE', motivo_quitado: null, precio_usd: '14.50', precio_nuevo_usd: null, etiqueta: 'Beige, M', producto: { id: 'demo-pr2', titulo: 'Blusa de lino con botones y manga corta', color_nombre: 'Beige', imagen: img(2) } },
    { id: 'demo-i3', pedido_cliente_id: 'demo-p4', cantidad: 3, estado: 'PENDIENTE', motivo_quitado: null, precio_usd: '14.83', precio_nuevo_usd: null, etiqueta: 'Azul, S', producto: { id: 'demo-pr3', titulo: 'Jeans de tiro alto y pierna recta', color_nombre: 'Azul', imagen: img(3) } },
    { id: 'demo-i4', pedido_cliente_id: 'demo-p5', cantidad: 2, estado: 'PENDIENTE', motivo_quitado: null, precio_usd: '15.50', precio_nuevo_usd: null, etiqueta: 'Blanco, L', producto: { id: 'demo-pr4', titulo: 'Zapatillas deportivas con suela gruesa', color_nombre: 'Blanco', imagen: img(4) } },
  ],
  ordenes: [orden('demo-o1', 'APROBADA', '28.3750', '5426.15', 6), orden('demo-o2', 'APROBADA', '16.6250', '3179.20', 3)],
  enviado_en: hace(6),
  confirmado_en: hace(5),
  motivo_cierre: null,
  para_retirar: null,
  version: 3,
};

export const PANEL_CICLO: PanelCiclo = {
  ciclo: { ...ciclo, max_prendas_por_vendedor: null, horas_para_cerrar: 3 * 24 + 5, horas_para_abrir: null },
  pedido: PEDIDO_CICLO,
  pedidos: [PEDIDO_CICLO],
  enviado_ciclo_usd: '133.50',
  pedidos_cliente_disponibles: [
    { id: 'demo-p1', pedido_ciclo_id: null, cliente_nombre: CLIENTES[5].nombre, prendas: 3, total_usd: '41.50', incluido: false, created_at: hace(0, 3) },
    { id: 'demo-p2', pedido_ciclo_id: null, cliente_nombre: CLIENTES[2].nombre, prendas: 2, total_usd: '27.00', incluido: false, created_at: hace(1) },
  ],
  minimo_pedido_usd: '66.00',
  politica: { pct_abono_ciclo: '0.250000', pct_abono_automatico: true, pct_abono_efectivo: '0.250000', es_nuevo: false, pct_minimo_para_retirar: '0.700000', dias_plazo_saldo: 15, limite_credito_usd: '150.00', max_prendas_por_ciclo: null, version: 1, personalizada: false },
};

// ── Pedidos de ciclos anteriores ─────────────────────────────────────────────
const resumenCiclo = (id: string, estado: PedidoCicloResumen['estado'], nombre: string, dias: number, total: string, aPagar: string, abonado: string, saldo: string, unidades: number): PedidoCicloResumen => ({
  id,
  estado,
  total_publico_usd: total,
  total_a_pagar_usd: aPagar,
  abono_minimo_usd: (Number(aPagar) * 0.25).toFixed(4),
  unidades,
  enviado_en: hace(dias + 4),
  confirmado_en: hace(dias + 3),
  ciclo_nombre: nombre,
  cierre_en: hace(dias),
  abonado_usd: abonado,
  saldo_usd: saldo,
});
export const PEDIDOS_CICLO: PedidoCicloResumen[] = [
  resumenCiclo('demo-pc1', 'CONFIRMADO', 'Ciclo 15 de octubre', -3, '133.50', '113.50', '45.0000', '68.5000', 9),
  resumenCiclo('demo-pc2', 'EN_PUNTO', 'Ciclo 30 de septiembre', 12, '96.00', '81.50', '57.0500', '24.4500', 7),
  resumenCiclo('demo-pc3', 'ENTREGADO', 'Ciclo 15 de septiembre', 27, '148.00', '126.00', '126.0000', '0.0000', 11),
  resumenCiclo('demo-pc4', 'ENTREGADO', 'Ciclo 30 de agosto', 43, '72.50', '61.50', '61.5000', '0.0000', 5),
];

// ── Estado de cuenta ─────────────────────────────────────────────────────────
export const ESTADO_CUENTA: EstadoCuenta = {
  vendedor: { id: 'demo-v', codigo: 'T998J7', nombre: 'Lorena', estado: 'ACTIVO' },
  saldos: {
    deuda_abierta_usd: '92.9500',
    deuda_vencida_usd: '0.0000',
    saldo_a_favor_usd: '3.5000',
    comisiones_disponibles_usd: '18.5000',
    comisiones_pendientes_usd: '6.0000',
    puntos_disponibles: '42350',
    puntos_pendientes: '11250',
  },
  tasa_bcv: '191.23000000',
  pedidos: [
    { id: 'demo-pc2', estado: 'EN_PUNTO', ciclo_nombre: 'Ciclo 30 de septiembre', total_a_pagar_usd: '81.5000', cargo_neto_usd: '81.5000', abonado_usd: '57.0500', saldo_usd: '24.4500', minimo_para_retirar_usd: '57.0500', faltante_hoy_usd: '0.0000', faltante_hoy_ves: null, compromiso: null },
    { id: 'demo-pc1', estado: 'CONFIRMADO', ciclo_nombre: 'Ciclo 15 de octubre', total_a_pagar_usd: '113.5000', cargo_neto_usd: '113.5000', abonado_usd: '45.0000', saldo_usd: '68.5000', minimo_para_retirar_usd: '79.4500', faltante_hoy_usd: '34.4500', faltante_hoy_ves: '6587.87', compromiso: null },
  ],
  movimientos: {
    data: [
      { id: 'demo-m1', libro: 'CUENTAS', tipo: 'ABONO', monto_usd: '-16.6250', monto_ves: '3179.20', descripcion: null, pedido_ciclo_id: 'demo-pc1', ciclo_nombre: 'Ciclo 15 de octubre', created_at: hace(3) },
      { id: 'demo-m2', libro: 'CUENTAS', tipo: 'ABONO', monto_usd: '-28.3750', monto_ves: '5426.15', descripcion: null, pedido_ciclo_id: 'demo-pc1', ciclo_nombre: 'Ciclo 15 de octubre', created_at: hace(5) },
      { id: 'demo-m3', libro: 'CUENTAS', tipo: 'CARGO_PEDIDO', monto_usd: '113.5000', monto_ves: null, descripcion: null, pedido_ciclo_id: 'demo-pc1', ciclo_nombre: 'Ciclo 15 de octubre', created_at: hace(5) },
      { id: 'demo-m4', libro: 'COMISIONES', tipo: 'COMISION_LIBERADA', monto_usd: '4.5000', monto_ves: null, descripcion: 'Por un pedido de Andrea, de tu red', pedido_ciclo_id: null, ciclo_nombre: null, created_at: hace(8) },
      { id: 'demo-m5', libro: 'CUENTAS', tipo: 'CREDITO_AGOTADO', monto_usd: '-3.5000', monto_ves: null, descripcion: 'Una prenda se agotó en SHEIN', pedido_ciclo_id: 'demo-pc2', ciclo_nombre: 'Ciclo 30 de septiembre', created_at: hace(10) },
      { id: 'demo-m6', libro: 'CUENTAS', tipo: 'ABONO', monto_usd: '-57.0500', monto_ves: '10850.30', descripcion: null, pedido_ciclo_id: 'demo-pc2', ciclo_nombre: 'Ciclo 30 de septiembre', created_at: hace(15) },
    ],
    meta: { page: 1, limit: 20, total: 6, totalPages: 1 },
  },
  puntos: [],
};

// ── Mi red ───────────────────────────────────────────────────────────────────
export const RED: MiRed = {
  codigo_vendedor: 'T998J7',
  actividad: { activa: true, vence_en: en(24), dias_restantes: 24, horas_restantes: 6, dias_actividad: 30 },
  comisiones: { disponibles_usd: '18.5000', pendientes_usd: '6.0000', retiradas_usd: '42.0000' },
  afiliados: [
    { id: 'demo-a1', nombre: 'Andrea Salazar', alta_en: hace(70), pendiente_usd: '3.0000', liberada_usd: '22.5000', estado_texto: null },
    { id: 'demo-a2', nombre: 'Daniela Ortiz', alta_en: hace(45), pendiente_usd: '1.5000', liberada_usd: '12.0000', estado_texto: null },
    { id: 'demo-a3', nombre: 'Rosa Medina', alta_en: hace(20), pendiente_usd: '1.5000', liberada_usd: '0.0000', estado_texto: 'Todavía no envía su primer pedido' },
  ],
};

// ── Puntos y premios ─────────────────────────────────────────────────────────
export const PUNTOS: MisPuntos = {
  disponibles: '42350',
  pendientes: '11250',
  por_vencer_30_dias: '5200',
  movimientos: [
    { id: 'demo-pt1', tipo: 'ACREDITACION', puntos: '9875', vence_en: en(170), created_at: hace(12) },
    { id: 'demo-pt2', tipo: 'CANJE_RESERVA', puntos: '-25000', vence_en: null, created_at: hace(18) },
    { id: 'demo-pt3', tipo: 'ACREDITACION', puntos: '14200', vence_en: en(150), created_at: hace(27) },
    { id: 'demo-pt4', tipo: 'ACREDITACION', puntos: '6900', vence_en: en(135), created_at: hace(43) },
  ],
};
const premio = (id: string, nombre: string, puntos: string, n: number, faltan: string | undefined, stock = 8): Premio => ({
  id,
  nombre,
  descripcion: null,
  fotos: [`tutorial/premio-${n}.svg`],
  costo_puntos: puntos,
  stock,
  limite_mensual_vendedor: 1,
  activo: true,
  version: 1,
  agotado: false,
  te_faltan: faltan,
  limite_alcanzado: false,
});
// D-182: ideas de premios de ejemplo
export const IDEAS: IdeaPremio[] = [
  { id: 'demo-idea-1', texto: 'Una plancha de cabello', enlace: null, estado: 'AGREGADA', created_at: '2026-09-20T15:00:00Z', decidida_en: '2026-09-22T15:00:00Z' },
  { id: 'demo-idea-2', texto: 'Unos audífonos inalámbricos', enlace: null, estado: 'PENDIENTE', created_at: '2026-10-01T15:00:00Z', decidida_en: null },
];

export const PREMIOS: Premio[] = [
  premio('demo-pm1', 'Cosmetiquera de viaje', '25000', 1, undefined),
  premio('demo-pm2', 'Audífonos inalámbricos', '60000', 2, '17650'),
  premio('demo-pm3', 'Bolso tote de lona', '35000', 3, undefined, 3),
];
export const CANJES: Canje[] = [
  { id: 'demo-cj1', vendedor_id: 'demo-v', premio_id: 'demo-pm1', premio_nombre: 'Cosmetiquera de viaje', puntos: '25000', estado: 'ENTREGADO', motivo_rechazo: null, lote_id: null, punto_entrega_id: null, created_at: hace(18), entregado_en: hace(10) },
];
